import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faDownload, faNewspaper, faBook, faFileContract, faMicrophone, faVideo, faSpinner, faFileText } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../api/axios';
import CardThumbnail, { getThumbnailUrl } from './CardThumbnail';
import { getYouTubeThumbnail, getYouTubeVideoId } from './YouTubePlayer';
import VideoPlayModal from './VideoPlayModal';
import { readPath } from '../seo/config';

const isVideoType = (name) => String(name || '').toLowerCase().includes('video');
const isAudioType = (name) => String(name || '').toLowerCase().includes('audio');
const isCuttingType = (name) => String(name || '').toLowerCase().includes('cutting');

const ContentCard = ({ data }) => {
    const { t } = useTranslation();
    const tr = (key) => t(`contentCard:${key}`);

    const navigate = useNavigate();

    const [downloadingCards, setDownloadingCards] = React.useState(new Set());
    const [playing, setPlaying] = React.useState(null);

    const isDesktop = () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1200px)').matches;

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
    };

    const handleRead = (item) => {
        const typeName = item.material_type?.name || '';
        navigate(readPath(item.id, item.title, typeName), {
            state: {
                id: item.id,
                documentType: typeName,
                url: item.url || null
            }
        });
    };

    const handleQuickPlay = (item, youtubeId) => {
        if (isDesktop()) {
            setPlaying({ id: item.id, title: item.title, youtubeId });
            return;
        }
        handleRead(item);
    };

    const handleDownload = async (data) => {
        setDownloadingCards(prev => new Set(prev).add(data.id));
        try {
            if (!data.manual?.uri) {
                console.error('No manual URI found');
                return;
            }

            const res = await axiosInstance.get(`/fsmanuals/${data.manual.uri}`, {
                responseType: 'blob',
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                    Expires: '0'
                }
            });

            // Create a blob URL for the file
            const fileBlob = new Blob([res.data], { type: res.data.type });
            const url = URL.createObjectURL(fileBlob);

            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = data.title || 'document.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the blob URL
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading manual:', error);
        } finally {
            setDownloadingCards(prev => {
                const newSet = new Set(prev);
                newSet.delete(data.id);
                return newSet;
            });
        }
    };

    const getIcon = (type) => {
        const materialType = type.toLowerCase();
        if (materialType.includes('journal')) {
            return faNewspaper;
        } else if (materialType.includes('book')) {
            return faBook;
        } else if (materialType.includes('report')) {
            return faFileText;
        } else if (materialType.includes('case') || materialType.includes('court')) {
            return faFileContract;
        } else if (materialType.includes('cutting')) {
            return faNewspaper;
        } else if (materialType.includes('audio')) {
            return faMicrophone;
        } else if (materialType.includes('video')) {
            return faVideo;
        } else {
            return faNewspaper;
        }
    };

    return (
        <motion.div initial="hidden" animate="visible">
            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 pt-6">
                {data?.map((d) => {
                    const year = d.date ? new Date(d.date).getFullYear() : 'Unknown';
                    const isVideo = isVideoType(d.material_type?.name);
                    const isAudio = isAudioType(d.material_type?.name);
                    const isCutting = isCuttingType(d.material_type?.name);
                    const canDownload = !isVideo && !isAudio && !isCutting;
                    const youtubeId = isVideo || isAudio ? getYouTubeVideoId(d.url) : null;
                    const imageSrc = youtubeId ? getYouTubeThumbnail(youtubeId) : getThumbnailUrl(d?.thumbnail?.uri);

                    return (
                        <motion.div
                            key={d.id}
                            variants={cardVariants}
                            whileHover={{ scale: 1.03 }}
                            className="rounded-lg overflow-hidden transition-all duration-300
                                border border-gray-200 dark:border-gray-700
                                bg-white dark:bg-gray-800
                                shadow-lg hover:shadow-xl"
                        >
                            <div className="relative p-4">
                                {/* Journal Icon */}
                                <div className="absolute top-2 right-2 text-gray-500 dark:text-gray-400 z-10">
                                    <FontAwesomeIcon icon={getIcon(d.material_type.name)} className="w-5 h-5" />
                                </div>

                                <CardThumbnail
                                    icon={getIcon(d.material_type.name)}
                                    title={d.title}
                                    imageSrc={imageSrc}
                                    onPlay={youtubeId ? () => handleQuickPlay(d, youtubeId) : undefined}
                                    playLabel={isVideo ? tr('watch') : isAudio ? tr('listen') : tr('read')}
                                />
                                {/* Content */}
                                <div className="mt-4">
                                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 truncate">
                                        <Link
                                            to={readPath(d.id, d.title, d.material_type?.name)}
                                            state={{ id: d.id, documentType: d.material_type?.name, url: d.url || null }}
                                            className="hover:text-[#41A6D7]"
                                        >
                                            {d?.title || ''}
                                        </Link>
                                    </h3>
                                    <p
                                        className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed overflow-hidden"
                                        style={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical'
                                        }}
                                    >
                                        {d?.description || ''}
                                    </p>

                                    {/* Meta Info */}
                                    <div className="flex justify-between items-center mt-3">
                                        <span className="text-sm text-gray-500 dark:text-gray-400">{year}</span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center justify-between mt-4">
                                    <Link
                                        to={readPath(d.id, d.title, d.material_type?.name)}
                                        state={{ id: d.id, documentType: d.material_type?.name, url: d.url || null }}
                                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-500 text-sm flex items-center"
                                    >
                                        <FontAwesomeIcon icon={faEye} className="w-3 h-3 mr-1" />
                                        {isVideo ? tr('watch') : isAudio ? tr('listen') : tr('read')}
                                    </Link>

                                    {canDownload && (
                                        <button 
                                            className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-500 text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed" 
                                            onClick={() => handleDownload(d)}
                                            disabled={downloadingCards.has(d.id)}
                                        >
                                            <FontAwesomeIcon 
                                                icon={downloadingCards.has(d.id) ? faSpinner : faDownload} 
                                                className={`w-3 h-3 mr-1 ${downloadingCards.has(d.id) ? 'animate-spin' : ''}`} 
                                            />
                                            {tr('download')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </motion.div>
            {playing ? (
                <VideoPlayModal
                    videoId={playing.youtubeId}
                    title={playing.title}
                    onClose={() => setPlaying(null)}
                />
            ) : null}
        </motion.div>
    );
};

export default memo(ContentCard);
